import { RiArrowDownSLine, RiArrowRightSLine } from "@remixicon/react";
import React, { ReactNode, useMemo, useState } from "react";
import classNames from "classnames";
import { FormFieldOptionInput } from "../../../../__generated__/api";
import { ReactComponent as SubtaskIcon } from "../../../../assets/icons/subtask.svg";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import EditableControl, { ControlType } from "../../../../components/editable-text/EditableText";
import Badge from "../../../../components/theme/badge/Badge";
import Button from "../../../../components/theme/button/Button";
import Icon from "../../../../components/theme/icon/Icon";
import SortableList from "../../../../components/theme/sortable/SortableList";
import Text from "../../../../components/theme/text/Text";
import Styles from "./CustomField.module.scss";
import useChangingCallback from "../../../../use-changing-callback";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import { flattenOptions, optionsFromString } from "./serialize";

export interface OptionValue {
    option: FormFieldOptionInput;
    levels: string[] | undefined;
}

export function Option({
    option,
    levels,
    onChange,
    childrenLevels,
    level,
    onDelete,
}: OptionValue & {
    onChange: (value: OptionValue) => void;
    onDelete: () => void;
    childrenLevels: number;
    level: number;
}) {
    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("value", () => option.value)
            .property("label", () => option.label)
            .property("description", () => option.description)
            .array("children", () => option.children),
        [],
    );
    tracker.useOnChange(() => {
        onChange({
            option: tracker.serialize(),
            levels,
        });
    }, []);

    const [isExpanded, setIsExpanded] = useState(false);

    const [isEditing, setIsEditing] = useState(!option.label && !option.description);
    const canHaveChildren = childrenLevels === -1 || childrenLevels > level;
    const childrenCount = properties.children.value?.length ?? 0;

    return (
        <div>
            <div
                className={Styles.option}
                key={option.value}
            >
                <SortableList.Listener className={Styles.draggable}>
                    <Icon.Draggable />
                </SortableList.Listener>
                <Button
                    prefixIcon={!isExpanded ? <RiArrowRightSLine /> : <RiArrowDownSLine />}
                    size="small"
                    variant="ghost-gray"
                    className={canHaveChildren && childrenCount > 0 ? "" : Styles.hidden}
                    onClick={() => setIsExpanded(!isExpanded)}
                />
                <div className={Styles["option-text"]}>
                    <Text textVariant="body-2-semibold">
                        <EditableControl
                            isFormField
                            shouldFocus
                            isEditing={isEditing}
                            shouldHandleEnter
                            placeholder="Enter an option label"
                            type={ControlType.TEXT}
                            maxLength={75}
                            inputClassName={Styles["option-input"]}
                            value={properties.label.value ?? ""}
                            onChange={value => {
                                properties.value.setValue(value as string);
                                properties.label.setValue(value as string);
                                setIsEditing(false);
                            }}
                        />
                    </Text>
                    <Text textVariant="body-2-regular" colorVariant="body-secondary">
                        <EditableControl
                            isFormField
                            shouldFocus={false}
                            shouldHandleEnter
                            placeholder="Enter an description (optional, 75 characters max)"
                            type={ControlType.TEXT}
                            maxLength={75}
                            inputClassName={Styles["option-input"]}
                            value={properties.description.value ?? ""}
                            onChange={value => {
                                properties.description.setValue(value as string);
                            }}
                        />
                    </Text>
                </div>
                {canHaveChildren && (
                    <>
                        <Button.Add
                            className={Styles["option-action"]}
                            iconVariant="default"
                            size="small"
                            onClick={() => {
                                setIsExpanded(true);
                                const copy = properties.children.value ? [...properties.children.value] : [];
                                copy.push({
                                    value: `Untitled${copy.length}`,
                                    label: "",
                                    description: "",
                                    children: [],
                                });
                                properties.children.setValue(copy);
                            }}
                        />
                        <Button.Edit
                            className={Styles["option-action"]}
                            iconVariant="default"
                            size="small"
                            onClick={() => setIsEditing(!isEditing)}
                        />
                        <Button.Delete
                            className={Styles["option-action"]}
                            iconVariant="default"
                            size="small"
                            onClick={() => onDelete()}
                        />
                        {childrenCount > 0 && (
                            <Badge variant="primary" prefixIcon={<SubtaskIcon />}>
                                {childrenCount}
                            </Badge>
                        )}
                    </>
                )}

            </div>
            <div className={Styles["option-children"]}>
                {isExpanded && canHaveChildren && option.children && (
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    <OptionsList
                        childrenLevels={childrenLevels}
                        level={level + 1}
                        levels={levels}
                        onChange={values => {
                            properties.children.setValue(values.options);
                        }}
                        options={option.children}
                    />
                )}
            </div>
        </div>
    );
}

export interface Value {
    options: FormFieldOptionInput[] | undefined;
    levels: string[] | undefined;
}

export function OptionsList({
    options,
    levels,
    onChange,
    childrenLevels,
    level,
}: Value & {
    onChange: (value: Value) => void;
    childrenLevels: number
    level: number
}) {
    const items = useMemo(() => options?.map(option => ({
        option,
        id: option.value as string,
    })) ?? [], [options]);
    const onHandleChange = useChangingCallback((index, value: OptionValue) => {
        const copy = options ? [...options] : [];
        copy[index] = value.option;
        onChange({
            options: copy,
            levels: value.levels,
        });
    }, [options, onChange]);
    const onHandleDelete = useChangingCallback(index => {
        const copy = options ? [...options] : [];
        copy.splice(index, 1);
        onChange({
            options: copy,
            levels,
        });
    }, [options, onChange]);
    return (
        <SortableList
            onReorder={sorted => {
                onChange({
                    levels,
                    options: sorted.toIdList("option"),
                });
            }}
            id="sortable"
            items={items}
            className={Styles.options}
        >
            {items?.map((option, index) => (
                <SortableList.Item shouldDragWholeElement={false} item={option} key={option.id}>
                    <Option
                        option={option.option}
                        levels={levels ?? []}
                        onChange={children => onHandleChange(index, children)}
                        onDelete={() => onHandleDelete(index)}
                        childrenLevels={childrenLevels}
                        level={level}
                    />
                </SortableList.Item>
            ))}
        </SortableList>
    );
}

export default function OptionsDisplay({
    options,
    levels,
    onChange,
    childrenLevels,
    level,
    title,
}: Value & {
    onChange: (value: Value) => void;
    childrenLevels: number;
    level: number;
    title: ReactNode;
}) {
    const [isBulkEditing, setIsBulkEditing] = useState(false);
    const [bulkEdit, setBulkEdit] = useState("");

    return (
        <div className={Styles["option-container"]}>
            <div className={Styles["option-header"]}>
                <Text as="h3" textVariant="body-1-semibold">
                    {title}
                </Text>
                <Button.Edit
                    variant="ghost-blue"
                    size="small"
                    onClick={() => {
                        setIsBulkEditing(!isBulkEditing);
                        setBulkEdit(flattenOptions(options ?? [], ""));
                    }}
                >
                    Bulk Edit
                </Button.Edit>

            </div>
            {isBulkEditing ? (
                <div className={Styles["bulk-edit"]}>
                    <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                        Enter the list of options, 1 per line. Use a dash (-) to indicate a child option.
                    </Text>
                    <FormGroup>
                        <textarea
                            value={bulkEdit}
                            placeholder={"Option 1\n- Child Option 1\n- Child Option 2\nOption 2"}
                            onChange={evt => setBulkEdit(evt.target.value)}
                            className={classNames("form-control", Styles["bulk-edit-text"])}
                        />
                    </FormGroup>
                    <div className={Styles["bulk-edit-actions"]}>
                        <Button
                            variant="outline-gray"
                            size="small"
                            onClick={() => setIsBulkEditing(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            onClick={() => {
                                onChange({
                                    options: optionsFromString(bulkEdit?.split("\n")),
                                    levels,
                                });
                                setIsBulkEditing(false);
                            }}
                        >
                            Save Bulk Edits
                        </Button>
                    </div>
                </div>

            ) : (
                <div className={Styles["options-list"]}>
                    <OptionsList
                        childrenLevels={childrenLevels}
                        level={level}
                        levels={levels}
                        onChange={onChange}
                        options={options}
                    />
                    <Button.Add
                        onClick={() => {
                            const copy = options ? [...options] : [];
                            copy.push({
                                value: `Untitled${copy.length}`,
                                label: "",
                                description: "",
                                children: [],
                            });
                            onChange({
                                options: copy,
                                levels,
                            });
                        }}
                        variant="outline-blue"
                        size="small"
                    >
                        Add
                    </Button.Add>
                </div>
            )}
        </div>
    );
}
